import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Button, Typography } from '@mui/material';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { explainabilityService } from '../../services/explainabilityService';
import { Rationale } from './StageTraces';
import { ConfidencePill, PolicyRefs } from './DecisionDetails';
import { ExplainabilityDataElement, resultDecisions } from './explainabilityTypes';
import { dsVar, ink } from '../../theme/tokens';

/** Decisions shown before the rest are left to the Explainability tab. */
const DECISIONS_SHOWN = 2;

interface GroupReasoningSummaryProps {
    campaignId: string;
    groupIdentifier: string;
    activityTemplateCode?: string;
    /** Opens the Explainability tab on this group's full reasoning. */
    onShowFull?: () => void;
}

/**
 * Why an Insights row says what it says: the decisions that set its result, with
 * their rationale, confidence and cited policies. The full trace is a link away
 * on the Explainability tab, narrowed to this row.
 *
 * Never blocks the drawer: a failed or empty lookup leaves a quiet line. Styled
 * with sx: it renders inside a portalled drawer.
 */
const GroupReasoningSummary: React.FC<GroupReasoningSummaryProps> = ({ campaignId, groupIdentifier, activityTemplateCode, onShowFull }) => {
    const [stages, setStages] = useState<ExplainabilityDataElement[] | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        setStages(null);
        setFailed(false);
        explainabilityService
            .getStageContext({ campaignId, groupIdentifier, activityTemplateCode }, { signal: controller.signal })
            .then(setStages)
            .catch((err) => { if (!axios.isCancel(err)) setFailed(true); });
        return () => controller.abort();
    }, [campaignId, groupIdentifier, activityTemplateCode]);

    const decisions = stages ? resultDecisions(stages) : [];
    // Without a decision marked as setting the result, the producing step's own account of it.
    const fallback = stages && decisions.length === 0
        ? [...stages].reverse().find((stage) => stage.outcome?.nl_description)
        : undefined;

    let body: React.ReactNode;
    if (failed) {
        body = <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>Couldn't load the reasoning for this row.</Typography>;
    } else if (stages === null) {
        body = <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>Loading reasoning…</Typography>;
    } else if (stages.length === 0) {
        body = (
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                No reasoning was recorded for this row. It will appear after the campaign next runs.
            </Typography>
        );
    } else {
        body = (
            <>
                {decisions.slice(0, DECISIONS_SHOWN).map(({ stage, decision }) => {
                    const choice = decision.selected_technique?.name ?? decision.chosen_option;
                    return (
                        <Box key={decision.decision_id} sx={{ mb: 1.25 }}>
                            <Typography component="div" sx={{ fontSize: '0.8125rem', color: dsVar('fg'), fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {decision.subject || decision.decision_type}
                                {choice && <Box component="span" sx={{ color: dsVar('accent-fg') }}>→ {choice}</Box>}
                                <ConfidencePill score={decision.confidence_score} />
                            </Typography>
                            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mb: 0.5 }}>{stage}</Typography>
                            {decision.rationale_text && <Rationale text={decision.rationale_text} />}
                            <PolicyRefs codes={decision.policy_refs} />
                        </Box>
                    );
                })}
                {decisions.length > DECISIONS_SHOWN && (
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mb: 1 }}>
                        +{decisions.length - DECISIONS_SHOWN} more {decisions.length - DECISIONS_SHOWN === 1 ? 'decision' : 'decisions'} set this result
                    </Typography>
                )}
                {fallback && (
                    <Box sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.6 }}>{fallback.outcome?.nl_description}</Typography>
                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>{fallback.stage}</Typography>
                    </Box>
                )}
                {onShowFull && (
                    <Button
                        size="small"
                        onClick={onShowFull}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                        sx={{ color: dsVar('accent-fg'), textTransform: 'none', fontSize: '0.75rem', px: 0, minWidth: 0 }}
                    >
                        See full reasoning ({stages.length} {stages.length === 1 ? 'step' : 'steps'})
                    </Button>
                )}
            </>
        );
    }

    return (
        <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '10px', backgroundColor: dsVar('page'), p: 1.5, mt: 2.5 }}>
            <Typography component="h4" sx={{ fontSize: '0.6875rem', fontWeight: 700, color: dsVar('fg-2'), textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 0.75, m: 0, mb: 1 }}>
                <PsychologyAltIcon sx={{ fontSize: 16, color: ink('#34D399') }} />
                Why this result
            </Typography>
            {body}
        </Box>
    );
};

export default GroupReasoningSummary;
